import { rooms } from "@/assets/rooms";
import CTATour from "@/blocks/CTATour";
import Image from "next/image";
import Link from "next/link";
import { HiChevronLeft, HiChevronRight, HiOutlineArrowLeft, HiOutlineArrowRight } from "react-icons/hi2";
import { useEffect, useState } from "react";
import {
  PiCurrencyDollarSimpleBold,
  PiBedBold,
  PiCouchBold,
  PiShowerBold,
  PiSelectionBold,
} from "react-icons/pi";
import Timeline from "@/blocks/Timeline";
import CTAExpose from "@/blocks/CTAExpose";

export const getStaticPaths = async () => {
  const paths = rooms.map((room) => {
    return {
      params: { slug: room.slug },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async (context) => {
  const slug = context.params.slug;
  const room = rooms.find((room) => room.slug === slug);
  return {
    props: { room: { ...room, shortDescription: '' } },
  };
};

export default function Room({ room }) {
  const [currentPicture, setCurrentPicture] = useState(0);
  const [inactive, setInactive] = useState(false);

  // change current picture every three seconds
  useEffect(() => {
    if (inactive) return;
    const interval = setInterval(() => {
      setCurrentPicture((currentPicture) => {
        if (currentPicture === room.pictures.length - 1) {
          return 0;
        } else {
          return currentPicture + 1;
        }
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [inactive]);

  return (
    <div className="bg-gray-100">
      <div className="relative min-h-[80vh] w-screen overflow-hidden md:pb-56 lg:pb-20">
        <div className="h-[80vh] relative md:static">
          <button onClick={() => {
            setCurrentPicture(currentPicture === room.pictures.length - 1 ? 0 : currentPicture + 1);
            setInactive(true);
          }} className="absolute right-0 h-full w-20  md:w-40 z-20 flex items-center justify-center">
            <HiChevronRight className="text-teracotta-500 h-12 w-12" />
          </button>
          <Image
            src={`${room.pictures[currentPicture]}`}
            className="w-full h-full"
            style={{ objectFit: "cover" }}
            fill
          />
          <button onClick={() => {
            setCurrentPicture(currentPicture === 0 ? room.pictures.length - 1 : currentPicture - 1);
            setInactive(true);
          }} className="absolute left-0 h-full w-20 md:w-40 z-20 flex items-center justify-center">
            <HiChevronLeft className="text-teracotta-500 h-12 w-12" />
          </button>
        </div>
        <div className="absolute top-0 left-0 w-full h-[40%] bg-gradient-to-b from-black/70"></div>



        <div className="md:absolute bottom-0 lg:flex grid grid-cols-4 lg:flex-row items-center z-30 -mt-4 w-[105vw] ml-[-2.5vw] ">
          {room.pictures.map((picture, index) => {
            return (
              <div
                className="flex-1"
                onClick={() => {
                  setCurrentPicture(index);
                  setInactive(true);
                }}
              >
                <div
                  className={`flex justify-center items-end shadow-xl relative ${currentPicture === index
                    ? "border-teracotta-500 border-4 h-[4rem] z-[10] scale-110"
                    : "h-[4rem] "
                    } -skew-x-6 overflow-hidden bg-white hover:z-[12] w-auto hover:scale-110 duration-300 transform`}
                >
                  <Image
                    sizes="(max-width: 768px) 20vw, 10vw"
                    className="skew-x-6 fill-skew"
                    style={{ objectFit: "cover" }}
                    src={`${picture}`}
                    fill
                  />

                  <div
                    className={`absolute w-full h-[40%] bottom-0 left-0 bg-gradient-to-t from-black/70`}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="container flex flex-col gap-8 mt-8">
        <Link href={"/#units"} className="flex gap-2 group mb-4">
          <HiOutlineArrowLeft className="w-6 h-6 text-teracotta-600 transform group-hover:-translate-x-1 duration-150" />
          <span className="text-teracotta-600">Back to Units</span>
        </Link>
        <h2 className=" h1 tracking-tight sm:text-6xl font-trajanPro">
          {room.name}
        </h2>
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 ${room?.livingroom ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          <div className="relative p-2 bg-white -skew-x-6 border-2 border-teracotta-500">
            <div className="absolute bottom-0 left-0 w-full h-full "></div>
            <div className="skew-x-6 flex flex-col items-center gap-2">
              <PiCurrencyDollarSimpleBold className="text-teracotta-600 h-8 w-8"></PiCurrencyDollarSimpleBold>
              <div className="text-teracotta-600 font-bold whitespace-nowrap">
                ${room?.price}
              </div>
            </div>

          </div>
          <div className="p-2 bg-white -skew-x-6 border-2 border-teracotta-500">
            <div className="absolute bottom-0 left-0 w-full h-full "></div>
            <div className="skew-x-6 flex flex-col items-center gap-2">
              <PiBedBold className="text-teracotta-600 h-8 w-8"></PiBedBold>
              <div className="text-teracotta-600 font-bold">
                {room?.bedrooms}
              </div>
            </div>
          </div>

          <div className="p-2 bg-white -skew-x-6 border-2 border-teracotta-500">
            <div className="absolute bottom-0 left-0 w-full h-full "></div>
            <div className="skew-x-6 flex flex-col items-center gap-2">
              <PiShowerBold className="text-teracotta-600 h-8 w-8"></PiShowerBold>
              <div className="text-teracotta-600 font-bold">
                {room?.bathrooms}
              </div>
            </div>
          </div>

          <div className="p-2 bg-white -skew-x-6 border-2 border-teracotta-500">
            <div className="absolute bottom-0 left-0 w-full h-full "></div>
            <div className="skew-x-6 flex flex-col items-center gap-2">
              <PiSelectionBold className="text-teracotta-600 h-8 w-8"></PiSelectionBold>
              <div className="flex flex-row gap-2">
                <div className="text-teracotta-600 text-sm font-bold">
                  {room?.areaF}ft²
                </div>
                <div className="text-teracotta-600 text-sm font-bold">
                  {room?.areaM}m²
                </div>
              </div>
            </div>
          </div>

          {room?.livingroom > 0 ? (
            <div className="p-2 bg-white -skew-x-6 border-2">
              <div className="absolute bottom-0 left-0 w-full h-full bg-white border-teracotta-500 border-2"></div>
              <div className="skew-x-6 flex flex-col items-center gap-2">
                <PiCouchBold className="text-teracotta-600 h-8 w-8"></PiCouchBold>
                <div className="text-teracotta-600 font-bold">
                  {room?.livingroom}
                </div>
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="flex flex-col gap-8 mb-12">
          <div className="flex flex-col lg:flex-row gap-8">

            <div className="flex flex-col rounded overflow-hidden">
              <img
                src={`${room.floorPlan}`}
                className="lg:h-[40rem]"
                style={{ objectFit: "contain" }}
              />
            </div>
            <div className="flex-1 flex-col flex gap-4">
              <div className="mt-6">
                {room.description.map((el) => {
                  return (
                    <p className="mt-4">{el}</p>
                  )
                })}
              </div>
              <div>
                <span className="text-xl font-medium">Asking price: </span>
                <span className="text-teracotta-600 font-medium text-2xl underline">
                  ${room?.price}
                </span>
              </div>
              <div className="flex">

                <button onClick={() => {
                  const virtualTour = document.getElementById('ctaExpose');
                  virtualTour.scrollIntoView({ behavior: 'smooth' })
                }} className="btn-primary flex gap-2 group items-center">
                  <span>
                    Find out more
                  </span>
                  <HiOutlineArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transform duration-300" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex">
        </div>
      </div>
      <Timeline />
      <CTAExpose />
      {/* <CTATour /> */}
    </div>
  );
}
